import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PARENT_DIALOG_PANEL_CLASS } from '../../../shared/constants/parent-dialog.constants';
import {
  StepUpPasswordDialogComponent,
  StepUpPasswordDialogData,
} from '../components/step-up-password-dialog/step-up-password-dialog.component';
import {
  StepUpTotpDialogComponent,
  StepUpTotpDialogData,
} from '../components/step-up-totp-dialog/step-up-totp-dialog.component';
import {
  TotpEnrollDialogComponent,
  TotpEnrollDialogResult,
} from '../components/totp-enroll-dialog/totp-enroll-dialog.component';

export type ParentStepUpStatus = {
  mode: 'password' | 'totp' | string;
  totpFlagEnabled: boolean;
  totpEnrolled: boolean;
};

export type ParentTotpEnrollment = {
  otpauthUri: string;
  qrCodeDataUrl: string;
  secret: string;
};

export type StepUpCredentials =
  | { password: string; totpCode?: never }
  | { totpCode: string; password?: never };

@Injectable({ providedIn: 'root' })
export class StepUpPasswordService {
  #dialog = inject(MatDialog);
  #http = inject(HttpClient);

  async getStatus(): Promise<ParentStepUpStatus> {
    return lastValueFrom(
      this.#http
        .get<ParentStepUpStatus>(`${environment.baseUrl}/user/identity/step-up`)
        .pipe(take(1)),
    );
  }

  async startEnrollment(): Promise<ParentTotpEnrollment> {
    return lastValueFrom(
      this.#http
        .post<ParentTotpEnrollment>(
          `${environment.baseUrl}/user/identity/totp/enroll`,
          {},
        )
        .pipe(take(1)),
    );
  }

  async confirmEnrollment(code: string): Promise<void> {
    await lastValueFrom(
      this.#http
        .post(`${environment.baseUrl}/user/identity/totp/confirm`, { code })
        .pipe(take(1)),
    );
  }

  /**
   * Opens the correct step-up dialog based on `identity.step_up.totp`.
   * Returns credentials payload or null if cancelled.
   */
  async prompt(
    data: StepUpPasswordDialogData & Partial<StepUpTotpDialogData>,
  ): Promise<StepUpCredentials | null> {
    const status = await this.getStatus();

    if (status.mode === 'totp' || status.totpFlagEnabled) {
      if (!status.totpEnrolled) {
        const enrolled = await this.promptEnrollment();
        if (!enrolled) {
          return null;
        }
      }
      return this.promptTotp(data);
    }

    const password = await this.promptPassword(data);
    return password ? { password } : null;
  }

  private async promptPassword(
    data: StepUpPasswordDialogData,
  ): Promise<string | null> {
    const ref = this.#dialog.open(StepUpPasswordDialogComponent, {
      data,
      panelClass: PARENT_DIALOG_PANEL_CLASS,
      width: '420px',
      disableClose: true,
      autoFocus: 'dialog',
    });
    return (await firstValueFrom(ref.afterClosed())) ?? null;
  }

  private async promptTotp(
    data: Partial<StepUpTotpDialogData>,
  ): Promise<StepUpCredentials | null> {
    const ref = this.#dialog.open(StepUpTotpDialogComponent, {
      data: {
        titleKey: data.titleKey ?? 'app.identity.stepUp.totpTitle',
        bodyKey: data.bodyKey ?? 'app.identity.stepUp.totpBody',
        confirmKey: data.confirmKey,
      } satisfies StepUpTotpDialogData,
      panelClass: PARENT_DIALOG_PANEL_CLASS,
      width: '420px',
      disableClose: true,
      autoFocus: 'dialog',
    });
    const code = (await firstValueFrom(ref.afterClosed())) as string | null;
    return code ? { totpCode: code } : null;
  }

  private async promptEnrollment(): Promise<boolean> {
    let enrollment: ParentTotpEnrollment;
    try {
      enrollment = await this.startEnrollment();
    } catch {
      return false;
    }

    const ref = this.#dialog.open(TotpEnrollDialogComponent, {
      data: enrollment,
      panelClass: PARENT_DIALOG_PANEL_CLASS,
      width: '440px',
      disableClose: true,
      autoFocus: 'dialog',
    });
    const result = (await firstValueFrom(
      ref.afterClosed(),
    )) as TotpEnrollDialogResult | null;
    if (!result?.code) {
      return false;
    }
    try {
      await this.confirmEnrollment(result.code);
      return true;
    } catch {
      return false;
    }
  }
}
